import React, { useState, useMemo } from 'react';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Icon,
  IconButton,
  Spinner,
  Box,
  Center,
} from '@chakra-ui/react';
import { MdEdit } from 'react-icons/md';
import { FaCaretUp, FaCaretDown } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

type User = {
  dscPerfilAcesso: string;
  codPerfilAcesso: string;
  numRegistro: number;
};

interface TabelaPerfilUsuarioProps {
  listaPerfilUsuario: User[];
  listaVazia: string;
  loading: boolean;
}

type Ordenacao = { chave: keyof User; direcao: 'asc' | 'desc' } | null;

const TabelaPerfilUsuario = ({ listaPerfilUsuario, listaVazia, loading }: TabelaPerfilUsuarioProps) => {
  const navigate = useNavigate();
  const [ordenacao, setOrdenacao] = useState<Ordenacao>(null);
  const [colunaDestacada, setColunaDestacada] = useState<keyof User | null>(null);

  const manipularOrdenacao = (chave: keyof User) => {
    let direcao: 'asc' | 'desc' = 'asc';
    if (ordenacao && ordenacao.chave === chave && ordenacao.direcao === 'asc') {
      direcao = 'desc';
    }
    setOrdenacao({ chave, direcao });
  };

  const listaOrdenada = useMemo(() => {
    const copia = [...listaPerfilUsuario];
    if (ordenacao !== null) {
      copia.sort((a, b) => {
        const valorA = a[ordenacao.chave];
        const valorB = b[ordenacao.chave];
        if (typeof valorA === 'string' && typeof valorB === 'string') {
          const comparacao = valorA.localeCompare(valorB);
          return ordenacao.direcao === 'asc' ? comparacao : -comparacao;
        }
        if (valorA < valorB) return ordenacao.direcao === 'asc' ? -1 : 1;
        if (valorA > valorB) return ordenacao.direcao === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return copia;
  }, [listaPerfilUsuario, ordenacao]);

  const obterIndicadorOrdenacao = (chave: keyof User) => {
    if (ordenacao && ordenacao.chave === chave) {
      return ordenacao.direcao === 'asc' ? (
        <FaCaretUp color="#1789FC" style={{ marginLeft: '4px' }} />
      ) : (
        <FaCaretDown color="#1789FC" style={{ marginLeft: '4px' }} />
      );
    }
      return <FaCaretUp color="#4a5568" style={{ marginLeft: '4px' }} />;
  };

  const renderRows = () =>
    listaOrdenada.length > 0 ? (
      listaOrdenada.map((perfilUsuario) => (
        <Tr key={perfilUsuario.codPerfilAcesso}>
          <Td>{perfilUsuario.codPerfilAcesso}</Td>
          <Td>{perfilUsuario.dscPerfilAcesso}</Td>
          <Td>{perfilUsuario.numRegistro === 1 ? 'Ativo' : 'Inativo'}</Td>
          <Td>
            <IconButton
              aria-label="Botão Editar"
              bgColor="#fff"
              onClick={() => navigate(`editar-perfil-usuario/${perfilUsuario.codPerfilAcesso}`)}
            >
              <Icon as={MdEdit} fontSize="24px" color="#1789FC" />
            </IconButton>
          </Td>
        </Tr>
      ))
    ) : (
      <Tr>
        <Td colSpan={5} color="#999898" textAlign="center">
          {listaVazia}
        </Td>
      </Tr>
    );

  return (
    <TableContainer>
      <Table variant="simple">
        <Thead>
          <Tr>
            <Th
              _hover={{ transition: '0.1s', color: '#1468C9' }}
              onClick={() => manipularOrdenacao('codPerfilAcesso')}
              onMouseEnter={() => setColunaDestacada('codPerfilAcesso')}
              onMouseLeave={() => setColunaDestacada(null)}
              cursor="pointer"
            >
              <Box display="flex" alignItems="center" color={ordenacao?.chave === 'codPerfilAcesso' ? 'blue.500' : 'inherit'}>
                ID{obterIndicadorOrdenacao('codPerfilAcesso')}
              </Box>
            </Th>
            <Th
              _hover={{ transition: '0.1s', color: '#1468C9' }}
              onClick={() => manipularOrdenacao('dscPerfilAcesso')}
              onMouseEnter={() => setColunaDestacada('dscPerfilAcesso')}
              onMouseLeave={() => setColunaDestacada(null)}
              cursor="pointer"
            >
              <Box display="flex" alignItems="center" color={ordenacao?.chave === 'dscPerfilAcesso' ? 'blue.500' : 'inherit'}>
                Perfil{obterIndicadorOrdenacao('dscPerfilAcesso')}
              </Box>
            </Th>
            <Th
              _hover={{ transition: '0.1s', color: '#1468C9' }}
              onClick={() => manipularOrdenacao('numRegistro')}
              onMouseEnter={() => setColunaDestacada('numRegistro')}
              onMouseLeave={() => setColunaDestacada(null)}
              cursor="pointer"
            >
              <Box display="flex" alignItems="center" color={ordenacao?.chave === 'numRegistro' ? 'blue.500' : 'inherit'}>
                Situação{obterIndicadorOrdenacao('numRegistro')}
              </Box>
            </Th>
            <Th>Editar</Th>
          </Tr>
        </Thead>
        <Tbody>
          {loading ? (
            <Tr>
              <Td colSpan={4} textAlign="center">
                <Center>
                  <Spinner size="lg" color="blue.500" />
                </Center>
              </Td>
            </Tr>
          ) : (
            renderRows()
          )}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

export default TabelaPerfilUsuario;
